// src/components/analyze/ResultTabs.jsx
// Tab Pills yang presisi dan konsisten untuk mobile

import React from 'react';
import { ChevronRight, Languages, Lightbulb, BookOpen, Hash } from 'lucide-react';
import { parseIrab } from '../../constants/nahwuDictionary';

const TABS = [
  { id: 'harakat',     label: 'Harakat',  icon: BookOpen  },
  { id: 'nahwu',       label: 'Nahwu',    icon: Hash      },
  { id: 'translation', label: 'Terjemah', icon: Languages },
  { id: 'meaning',     label: 'Arti',     icon: Lightbulb },
];

export default function ResultTabs({ activeTab, onTabChange, data }) {
  return (
    <div className="px-4 pt-2 pb-4 animate-fade-in">
      {/* ── Tab Navigation (Pills) ────────────────────── */}
      <div
        className="
          bg-white dark:bg-slate-800
          rounded-3xl shadow-soft
          border border-stone-100 dark:border-slate-700
          p-1.5 mb-4
        "
      >
        {/*
          PERBAIKAN PRESISI:
          - Gunakan `grid grid-cols-4` agar semua tab sama lebar
          - Tidak ada lagi `flex` dengan `flex-1` yang tidak konsisten
          - `overflow-hidden` pada container pills
        */}
        <div className="grid grid-cols-4 gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                aria-label={tab.label}
                className={`
                  relative
                  flex items-center justify-center
                  gap-1
                  h-[40px]
                  px-2 rounded-2xl
                  font-bold text-[11px]
                  whitespace-nowrap
                  transition-all duration-200
                  active:scale-95
                  ${isActive
                    ? 'bg-gradient-to-br from-green-400 to-green-600 text-white shadow-md shadow-green-500/25'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-stone-100 dark:hover:bg-slate-700'
                  }
                `}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="leading-none">{tab.label}</span>

                {/* Indikator Aktif di Bawah */}
                {isActive && (
                  <span
                    className="
                      absolute -bottom-1 left-1/2 -translate-x-1/2
                      w-1.5 h-1.5 bg-white rounded-full
                      shadow-[0_0_4px_rgba(255,255,255,0.6)]
                    "
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab Content ───────────────────────────────── */}
      <div className="animate-fade-in">
        {activeTab === 'harakat'     && <HarakatTab     data={data} />}
        {activeTab === 'nahwu'       && <NahwuTab       data={data} />}
        {activeTab === 'translation' && <TranslationTab data={data} />}
        {activeTab === 'meaning'     && <MeaningTab     data={data} />}
      </div>
    </div>
  );
}

// ── Harakat Tab ─────────────────────────────────────────────
function HarakatTab({ data }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 pt-4 pb-3 border-b border-stone-100 dark:border-slate-700">
        <p className="text-xs font-extrabold text-slate-600 dark:text-slate-300 flex items-center gap-2">
          📖 Teks Lengkap dengan Harakat
        </p>
      </div>
      <div className="p-4">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-2xl p-5">
          <p
            dir="rtl"
            className="text-amber-900 dark:text-amber-300 text-right leading-loose"
            style={{ fontFamily: 'Amiri, serif', fontSize: '24px', lineHeight: '2.2' }}
          >
            {data?.chunks?.map(c => c.arabic).join(' ') || 'Tidak ada data'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Nahwu Tab ───────────────────────────────────────────────
function NahwuTab({ data }) {
  return (
    <div className="space-y-3">
      {data?.chunks?.map((chunk, idx) => (
        <div key={idx} className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 overflow-hidden">
          <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-stone-100 dark:border-slate-700">
            <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wide">
              {chunk.title || `BAGIAN ${idx + 1}`}
            </span>
            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 bg-stone-100 dark:bg-slate-700 px-2.5 py-1 rounded-full">
              {chunk.irab?.length || 0} kata
            </span>
          </div>

          <div className="px-4 pt-4 pb-3">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40 rounded-2xl px-4 py-3">
              <p
                dir="rtl"
                className="text-green-900 dark:text-green-300 text-right leading-loose"
                style={{ fontFamily: 'Amiri, serif', fontSize: '22px', lineHeight: '2' }}
              >
                {chunk.arabic}
              </p>
            </div>
          </div>

          <div className="px-4 pb-4 space-y-2">
            {chunk.irab?.map((item, iIdx) => (
              <WordCard key={iIdx} word={item} index={iIdx} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Translation Tab ─────────────────────────────────────────
function TranslationTab({ data }) {
  return (
    <div className="space-y-3">
      {data?.chunks?.map((chunk, idx) => (
        <div key={idx} className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 overflow-hidden">
          <div className="px-4 pt-4 pb-3 border-b border-stone-100 dark:border-slate-700">
            <span className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-extrabold px-3 py-1 rounded-full uppercase tracking-wide">
              {chunk.title || `BAGIAN ${idx + 1}`}
            </span>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40 rounded-2xl px-4 py-3">
              <p
                dir="rtl"
                className="text-green-900 dark:text-green-300 text-right"
                style={{ fontFamily: 'Amiri, serif', fontSize: '22px', lineHeight: '2' }}
              >
                {chunk.arabic}
              </p>
            </div>
            {chunk.translation && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/40 rounded-2xl p-4">
                <p className="text-[10px] font-extrabold text-blue-500 dark:text-blue-400 uppercase tracking-widest mb-1.5">
                  Terjemahan
                </p>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300 leading-relaxed italic">
                  "{chunk.translation}"
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Meaning Tab (Kata + I'rab + Arti dari AI) ──────────────
function MeaningTab({ data }) {
  const allWords = data?.chunks?.flatMap(c => c.irab || []) ?? [];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 overflow-hidden">
      <div className="px-4 pt-4 pb-3 border-b border-stone-100 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <p className="text-xs font-extrabold text-slate-600 dark:text-slate-300 flex items-center gap-2">
            <span>📘</span> Kamus Mini Per Kata
          </p>
          <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 bg-stone-100 dark:bg-slate-700 px-2.5 py-1 rounded-full">
            {allWords.length} kata
          </span>
        </div>
      </div>

      <div className="divide-y divide-stone-100 dark:divide-slate-700">
        {allWords.map((item, idx) => {
          const parsed = parseIrab(item.r);

          return (
            <div
              key={idx}
              className="px-4 py-3 hover:bg-stone-50 dark:hover:bg-slate-700/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Kolom 1: Kata Arab */}
                <span
                  dir="rtl"
                  className="
                    flex-shrink-0
                    text-lg font-bold
                    text-green-800 dark:text-green-300
                    bg-green-100/80 dark:bg-green-900/30
                    px-2.5 py-1 rounded-xl
                    min-w-[60px] text-center
                  "
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  {item.p}
                </span>

                {/* Kolom 2: I'rab + Arti */}
                <div className="flex-1 min-w-0">
                  {/* I'rab (parsed.main) */}
                  <p
                    dir="rtl"
                    className="text-[12px] font-extrabold text-slate-700 dark:text-slate-200 text-right leading-tight"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    {parsed.main}
                  </p>

                  {/* ✨ ARTI dari AI (item.m) */}
                  {item.m && (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight">
                      → {item.m}
                    </p>
                  )}

                  {/* Detail tambahan */}
                  {parsed.detail && (
                    <p
                      dir="rtl"
                      className="text-[10px] text-slate-400 dark:text-slate-500 text-right leading-tight mt-0.5"
                      style={{ fontFamily: 'Amiri, serif' }}
                    >
                      {parsed.detail}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Word Card (Nahwu Tab) — Sekarang termasuk Arti ────────
function WordCard({ word, index }) {
  const parsed = parseIrab(word.r);

  return (
    <div
      className="
        bg-stone-50 dark:bg-slate-700/60
        hover:bg-green-50 dark:hover:bg-green-900/20
        border border-stone-100 dark:border-slate-600
        rounded-2xl px-3 py-2.5
        transition-all animate-fade-in
      "
      style={{ animationDelay: `${index * 0.04}s` }}
    >
      {/* Baris 1: Kata + Panah + I'rab */}
      <div className="flex items-center gap-2 mb-1">
        <span
          dir="rtl"
          className="text-lg font-bold text-green-800 dark:text-green-300 bg-green-100/80 dark:bg-green-900/30 px-2.5 py-0.5 rounded-xl flex-shrink-0"
          style={{ fontFamily: 'Amiri, serif' }}
        >
          {word.p}
        </span>
        <ChevronRight className="w-4 h-4 text-green-400 flex-shrink-0" />
        <span
          dir="rtl"
          className="text-[13px] font-extrabold text-slate-800 dark:text-slate-200 text-right flex-1"
          style={{ fontFamily: 'Amiri, serif' }}
        >
          {parsed.main}
        </span>
      </div>

      {/* ✨ Baris 2: Arti dari AI (jika ada) */}
      {word.m && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-1 leading-relaxed">
          → {word.m}
        </p>
      )}

      {/* Baris 3: Detail tambahan */}
      {(parsed.detail || parsed.connector) && (
        <p
          dir="rtl"
          className="text-[11px] text-slate-500 dark:text-slate-400 text-right leading-relaxed"
          style={{ fontFamily: 'Amiri, serif' }}
        >
          {parsed.connector && (
            <span className="font-bold text-slate-600 dark:text-slate-300">
              {parsed.connector}{' '}
            </span>
          )}
          {parsed.detail && <span>{parsed.detail}</span>}
        </p>
      )}
    </div>
  );
}